/**
 * Notification Settings Page - Manage notification preferences
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api/client';
import { QuietHoursEditor } from '@/features/settings/QuietHoursEditor';
import { DigestPreferences } from '@/features/settings/DigestPreferences';

interface NotificationPreferences {
  id: string;
  user_id: string;
  in_app_enabled: boolean;
  email_enabled: boolean;
  push_enabled: boolean;
  event_preferences: Record<string, boolean>;
  quiet_hours: {
    enabled: boolean;
    start: string;
    end: string;
    timezone: string | null;
  };
  digest: {
    enabled: boolean;
    frequency: string;
    time: string;
    day: number;
  };
  daily_briefing: {
    enabled: boolean;
    time: string;
    email: boolean;
  };
}

const EVENT_TYPES = [
  { key: 'task_assigned', label: 'Task Assigned', description: 'When you are assigned to a task' },
  { key: 'task_mentioned', label: 'Task Mentioned', description: 'When you are @mentioned in a task' },
  { key: 'task_due_soon', label: 'Task Due Soon', description: 'When a task is due within 48 hours' },
  { key: 'task_overdue', label: 'Task Overdue', description: 'When a task is past its due date' },
  { key: 'task_completed', label: 'Task Completed', description: 'When a task you\'re assigned to is completed' },
  { key: 'task_updated', label: 'Task Updated', description: 'When a task you\'re watching is updated' },
  { key: 'comment_added', label: 'New Comment', description: 'When someone comments on your task' },
  { key: 'comment_reply', label: 'Comment Reply', description: 'When someone replies to your comment' },
  { key: 'comment_mentioned', label: 'Comment Mentioned', description: 'When you are @mentioned in a comment' },
  { key: 'project_invited', label: 'Project Invitation', description: 'When you are invited to a project' },
  { key: 'meeting_transcript_ready', label: 'Transcript Ready', description: 'When a meeting transcript is ready' },
  { key: 'draft_tasks_extracted', label: 'Tasks Extracted', description: 'When AI extracts tasks from a meeting' },
];

export const NotificationSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<NotificationPreferences>('/notification-preferences');
      setPreferences(response.data);
    } catch (err) {
      console.error('Failed to fetch notification preferences:', err);
      setError(t('settings.notificationsPage.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (updates: Partial<NotificationPreferences>) => {
    if (!preferences) return;

    try {
      setSaving(true);
      const response = await apiClient.put<NotificationPreferences>('/notification-preferences', updates);
      setPreferences(response.data);
    } catch (err) {
      console.error('Failed to update notification preferences:', err);
      setError(t('settings.notificationsPage.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleChannelToggle = (channel: 'in_app_enabled' | 'email_enabled' | 'push_enabled') => {
    if (!preferences) return;
    updatePreferences({ [channel]: !preferences[channel] });
  };

  const handleEventToggle = async (eventType: string) => {
    if (!preferences) return;

    const newValue = !preferences.event_preferences[eventType];
    try {
      setSaving(true);
      const response = await apiClient.patch<NotificationPreferences>('/notification-preferences/events', {
        event_type: eventType,
        enabled: newValue,
      });
      setPreferences(response.data);
    } catch (err) {
      console.error('Failed to update event preference:', err);
      setError(t('settings.notificationsPage.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleQuietHoursUpdate = async (quietHours: NotificationPreferences['quiet_hours']) => {
    try {
      setSaving(true);
      const response = await apiClient.put<NotificationPreferences>('/notification-preferences/quiet-hours', quietHours);
      setPreferences(response.data);
    } catch (err) {
      console.error('Failed to update quiet hours:', err);
      setError(t('settings.notificationsPage.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDigestUpdate = async (digest: NotificationPreferences['digest']) => {
    try {
      setSaving(true);
      const response = await apiClient.put<NotificationPreferences>('/notification-preferences/digest', digest);
      setPreferences(response.data);
    } catch (err) {
      console.error('Failed to update digest settings:', err);
      setError(t('settings.notificationsPage.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDailyBriefingUpdate = async (field: string, value: boolean | string) => {
    if (!preferences) return;
    const updated = { ...preferences.daily_briefing, [field]: value };
    try {
      setSaving(true);
      const response = await apiClient.put<NotificationPreferences>('/notification-preferences/daily-briefing', updated);
      setPreferences(response.data);
    } catch (err) {
      console.error('Failed to update daily briefing settings:', err);
      setError(t('settings.notificationsPage.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const sendTestNotification = async () => {
    try {
      await apiClient.post('/notification-preferences/test-notification');
      alert(t('settings.notificationsPage.testSent'));
    } catch (err) {
      console.error('Failed to send test notification:', err);
      alert(t('settings.notificationsPage.testFailed'));
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error && !preferences) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchPreferences}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!preferences) return null;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('settings.notificationsPage.title', 'Notification Settings')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('settings.notificationsPage.subtitle', 'Manage how and when you receive notifications')}
          </p>
        </div>
        <button
          onClick={sendTestNotification}
          className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
        >
          {t('settings.notificationsPage.sendTest', 'Send Test')}
        </button>
      </div>

      {/* Saving indicator */}
      {saving && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg">
          {t('settings.notificationsPage.saving')}
        </div>
      )}

      {/* Notification Channels */}
      <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('settings.notificationsPage.channels', 'Notification Channels')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('settings.notificationsPage.channelsDesc', 'Choose how you want to receive notifications')}
          </p>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {/* In-app */}
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  {t('settings.notificationsPage.inApp', 'In-App Notifications')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.notificationsPage.inAppDesc', 'Receive notifications within the application')}
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.in_app_enabled}
                onChange={() => handleChannelToggle('in_app_enabled')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Email */}
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  {t('settings.notificationsPage.email', 'Email Notifications')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.notificationsPage.emailDesc', 'Receive notifications via email')}
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.email_enabled}
                onChange={() => handleChannelToggle('email_enabled')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Push */}
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  {t('settings.notificationsPage.push', 'Push Notifications')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.notificationsPage.pushDesc', 'Receive push notifications on your device')}
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.push_enabled}
                onChange={() => handleChannelToggle('push_enabled')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('settings.notificationsPage.eventTypes', 'Notification Types')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('settings.notificationsPage.eventTypesDesc', 'Choose which types of notifications you want to receive')}
          </p>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {EVENT_TYPES.map((event) => (
            <div key={event.key} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  {t(`settings.notificationsPage.events.${event.key}`, event.label)}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t(`settings.notificationsPage.eventsDesc.${event.key}`, event.description)}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.event_preferences[event.key] ?? true}
                  onChange={() => handleEventToggle(event.key)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
          ))}
        </div>
      </section>

      {/* Daily Briefing */}
      <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('settings.notificationsPage.dailyBriefing', 'Daily Briefing')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('settings.notificationsPage.dailyBriefingDesc', 'Get a daily summary of your tasks, meetings, and priorities')}
          </p>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {/* Enable/Disable */}
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  {t('settings.notificationsPage.dailyBriefingEnabled', 'Enable Daily Briefing')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.notificationsPage.dailyBriefingEnabledDesc', 'Receive an in-app notification with your daily focus')}
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.daily_briefing.enabled}
                onChange={() => handleDailyBriefingUpdate('enabled', !preferences.daily_briefing.enabled)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          {/* Time */}
          {preferences.daily_briefing.enabled && (
            <div className="p-6 flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  {t('settings.notificationsPage.dailyBriefingTime', 'Delivery Time')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.notificationsPage.dailyBriefingTimeDesc', 'When to send your daily briefing (local time)')}
                </p>
              </div>
              <input
                type="time"
                value={preferences.daily_briefing.time}
                onChange={(e) => handleDailyBriefingUpdate('time', e.target.value)}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
          )}

          {/* Email opt-in */}
          {preferences.daily_briefing.enabled && (
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    {t('settings.notificationsPage.dailyBriefingEmail', 'Email Delivery')}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('settings.notificationsPage.dailyBriefingEmailDesc', 'Also receive the daily briefing via email')}
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.daily_briefing.email}
                  onChange={() => handleDailyBriefingUpdate('email', !preferences.daily_briefing.email)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
              </label>
            </div>
          )}
        </div>
      </section>

      {/* Quiet Hours */}
      <QuietHoursEditor
        config={preferences.quiet_hours}
        onUpdate={handleQuietHoursUpdate}
      />

      {/* Digest Settings */}
      <DigestPreferences
        config={preferences.digest}
        onUpdate={handleDigestUpdate}
      />
    </div>
  );
};

export default NotificationSettingsPage;
